import {
  isContactImportJobExecutionStatus,
  isContactImportJobTerminalStatus,
} from "@multi-chat/shared";

export type ImportNotificationUpsertDecision = "skip" | "update" | "insert";

export function decideImportNotificationUpsert(input: {
  dismissedJobIds: ReadonlySet<string>;
  jobId: string;
  status: string;
  hasVisibleToast: boolean;
}): ImportNotificationUpsertDecision {
  if (isContactImportJobExecutionStatus(input.status)) {
    if (input.dismissedJobIds.has(input.jobId)) return "skip";
    return input.hasVisibleToast ? "update" : "insert";
  }
  if (isContactImportJobTerminalStatus(input.status)) {
    return input.hasVisibleToast ? "update" : "insert";
  }
  return "skip";
}

export function dismissedImportProgressJobIdsAfterDismiss(
  dismissedJobIds: ReadonlySet<string>,
  notification: { jobId?: string; status?: string; kind?: string },
): Set<string> {
  const next = new Set(dismissedJobIds);
  if (
    notification.kind === "import" &&
    notification.jobId &&
    notification.status != null &&
    isContactImportJobExecutionStatus(notification.status)
  ) {
    next.add(notification.jobId);
  }
  return next;
}

export function dismissedImportProgressJobIdsAfterTerminal(
  dismissedJobIds: ReadonlySet<string>,
  jobId: string,
  status: string,
): Set<string> {
  if (!isContactImportJobTerminalStatus(status)) return new Set(dismissedJobIds);
  const next = new Set(dismissedJobIds);
  next.delete(jobId);
  return next;
}

/** Hide the progress toast while the matching progress dialog is open. */
export function dismissedImportProgressJobIdsAfterDialogOpen(
  dismissedJobIds: ReadonlySet<string>,
  jobId: string,
): Set<string> {
  const next = new Set(dismissedJobIds);
  next.add(jobId);
  return next;
}

/** Re-enable the progress toast after Continue in background. */
export function dismissedImportProgressJobIdsAfterContinueInBackground(
  dismissedJobIds: ReadonlySet<string>,
  jobId: string,
): Set<string> {
  const next = new Set(dismissedJobIds);
  next.delete(jobId);
  return next;
}

export type ImportProgressToastSurfaceInput = {
  jobId: string;
  status: string;
  dialogOpenForJobId: string | null;
  dismissedProgressJobIds: ReadonlySet<string>;
};

/**
 * Progress toast and progress dialog are exclusive for the same job.
 * X keeps the toast dismissed until Continue in background explicitly restores it.
 */
export function contactImportProgressSurfaces(input: ImportProgressToastSurfaceInput): {
  showToast: boolean;
  showDialog: boolean;
} {
  const showDialog = input.dialogOpenForJobId === input.jobId;
  if (!isContactImportJobExecutionStatus(input.status)) {
    return { showToast: false, showDialog };
  }
  if (showDialog) {
    return { showToast: false, showDialog: true };
  }
  if (input.dismissedProgressJobIds.has(input.jobId)) {
    return { showToast: false, showDialog: false };
  }
  return { showToast: true, showDialog: false };
}
